// Mirrors the backend's CourseCode rules (Slugify + NormalizeSlug) so what the user sees is what
// gets saved.

export const COURSE_SLUG_MAX_LENGTH = 45

// Letters NFD can't split into base + accent.
const SPECIAL_LETTERS = {
  ı: 'i',
  ø: 'o',
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  đ: 'd',
  ł: 'l',
}

/**
 * Splits a course code into the editable slug and the locked numeric suffix:
 * - "my-course-61564577"            new courses (8 digits)
 * - "my-course-638512345678901234"  Jan 2023 - June 2025 (18-digit tick count)
 * - "my-course638012345678901234"   mid 2022 (tick count, no hyphen)
 * - "my-course-dbc855"              any last segment with a digit (e.g. ai-assistant's hex)
 * Codes with none of these have no suffix; the whole code becomes the slug and the backend adds
 * a fresh one on save.
 */
export const splitCourseCode = code => {
  if (!code) return null

  const match =
    /^(.*)-(\d{8,})$/.exec(code) ||
    /^(.*?)(\d{18})$/.exec(code) ||
    /^(.*)-([A-Za-z0-9]*\d[A-Za-z0-9]*)$/.exec(code)
  if (match) return { slug: match[1], suffix: match[2] }

  return { slug: normalizeCourseSlug(code), suffix: null }
}

/**
 * Normalizes user input into a slug: no accents, lowercase, only a-z, 0-9 and single hyphens,
 * at most 45 characters. Pass keepTrailingHyphen while typing so "my " can become "my-course".
 */
export const normalizeCourseSlug = (
  value,
  { keepTrailingHyphen = false } = {},
) => {
  const slug = (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[ıøßæœđł]/g, letter => SPECIAL_LETTERS[letter])
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, COURSE_SLUG_MAX_LENGTH)

  return keepTrailingHyphen ? slug : slug.replace(/-+$/, '')
}
